import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  Row,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";
import CustomInput from "../../CommonComponents/CustomForm/CustomInputField";
import CustomTooltip from "../../Elements/Tooltip";
import styles from "./StatsFilter.module.css";

// Basic email format validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getMaxDateToday = () => {
  const d = new Date();
  return d.toISOString().split("T")[0]; // YYYY-MM-DD for input max
};

// Display stored YYYY-MM-DD as DD-MM-YYYY in the UI (hyphen separator)
const formatDateForDisplay = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string") return "";
  const parts = isoDate.trim().split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  if (y.length === 4 && m.length <= 2 && d.length <= 2) {
    return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
  }
  return isoDate;
};

const sortingOptions = [
  { key: "first_last_asc", label: "First name, Last name", icon: faArrowUp },
  { key: "first_last_desc", label: "First name, Last name", icon: faArrowDown },
  { key: "last_first_asc", label: "Last name, First name", icon: faArrowUp },
  { key: "last_first_desc", label: "Last name, First name", icon: faArrowDown },
  { key: "email_asc", label: "Email", icon: faArrowUp },
  { key: "email_desc", label: "Email", icon: faArrowDown },
  { key: "percent_asc", label: "Percent viewed", icon: faArrowUp },
  { key: "percent_desc", label: "Percent viewed", icon: faArrowDown },
  { key: "viewed_time_asc", label: "Time Viewed", icon: faArrowUp },
  { key: "viewed_time_desc", label: "Time Viewed", icon: faArrowDown },
];

const StatsFilters = ({
  threshold,
  onFilterChange,
  sorting = false,
  defaultDirection,
  token,
}) => {
  const { hash } = useLocation();
  const [filters, setFilters] = useState({
    email: "",
    location: "",
    seen_pages: "",
    seen_mins: "",
    on_page: "",
    from_date: "",
    to_date: "",
    direction: defaultDirection,
    recipient: "",
    video_views: "",
  });
  const [engagementValue, setEngagementValue] = useState(threshold ?? "");
  const [errors, setErrors] = useState({});
  const fromDatePickerRef = useRef(null);
  const toDatePickerRef = useRef(null);

  useEffect(() => {
    setEngagementValue(threshold ?? "");
  }, [threshold]);

  // Numeric filter fields that must be >= 1 (no 0 or negative)
  const MIN_NUMERIC_FIELDS = ["seen_pages", "seen_mins", "on_page", "video_views"];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (MIN_NUMERIC_FIELDS.includes(name)) {
      const num = value === "" ? null : Number(value);
      if (num !== null && (Number.isNaN(num) || num < 1)) return;
      if (name === "video_views" && num !== null && num > 100) return;
    }
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleEngagementChange = (e) => {
    const value = e.target.value;
    const num = value === "" ? null : Number(value);
    if (num !== null && (Number.isNaN(num) || num < 1)) return;
    setEngagementValue(value);
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailTrim = (filters.email || "").trim();
    const recipientTrim = (filters.recipient || "").trim();
    const maxDate = getMaxDateToday();

    if (emailTrim && !EMAIL_REGEX.test(emailTrim)) {
      nextErrors.email = "Please enter a valid email format.";
    }
    if ((filters.recipient || "").length > 0 && !recipientTrim) {
      nextErrors.recipient = "Recipient name cannot be empty.";
    }
    if (filters.from_date && filters.from_date > maxDate) {
      nextErrors.from_date = "From date cannot be in the future.";
    }
    if (filters.to_date && filters.to_date > maxDate) {
      nextErrors.to_date = "To date cannot be in the future.";
    }
    if (filters.from_date && filters.to_date && filters.from_date > filters.to_date) {
      nextErrors.to_date = "To date must be on or after From date.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Handle Apply Filters
  const handleApplyFilters = () => {
    if (!validateForm()) return;
    if (onFilterChange) {
      onFilterChange({ ...filters, threshold: engagementValue });
    }
  };

  // Handle Reset Filters
  const handleResetFilters = () => {
    setErrors({});
    const defaultFilters = {
      email: "",
      location: "",
      seen_pages: "",
      seen_mins: "",
      on_page: "",
      from_date: "",
      to_date: "",
      direction: defaultDirection,
      recipient: "",
      video_views: "",
      threshold: "",
    };
    setFilters(defaultFilters); // Reset state
    setEngagementValue(threshold ?? ""); // Reset engagement input to server value
    if (onFilterChange) {
      onFilterChange(defaultFilters, "reset"); // Notify parent with reset filters
    }
  };

  const selectedOption = sortingOptions.find(
    (option) => option.key === filters.direction
  );

  const maxDateToday = getMaxDateToday();
  const fromDateMax = filters.to_date && filters.to_date <= maxDateToday ? filters.to_date : maxDateToday;
  const toDateMin = filters.from_date || undefined;

  return (
    <Form className={`${styles.filterForm} p-3`}>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <label>Email</label>
            <Form.Control
              className={styles.customInput}
              type="email"
              name="email"
              placeholder="Email"
              value={filters.email}
              onChange={handleInputChange}
              isInvalid={!!errors.email}
            />
            {errors.email && (
              <Form.Text className="text-danger">{errors.email}</Form.Text>
            )}
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            {sorting ? (
              <DropdownButton
                className={styles.directionDropDown}
                id="dropdown-basic-button"
                title={
                  selectedOption ? (
                    <>
                      <FontAwesomeIcon
                        icon={selectedOption.icon}
                        className="me-2"
                      />
                      {selectedOption.label}
                    </>
                  ) : (
                    "Select Sorting"
                  )
                }
                onSelect={(eKey) =>
                  handleInputChange({
                    target: { name: "direction", value: eKey },
                  })
                }
                variant="secondary"
              >
                {sortingOptions.map((option) => (
                  <Dropdown.Item key={option.key} eventKey={option.key}>
                    <FontAwesomeIcon icon={option.icon} className="me-2" />
                    {option.label}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            ) : (
              <div className="mb-3">
                <label>Recipient</label>
                <Form.Control
                  className={styles.customInput}
                  type="text"
                  name="recipient"
                  placeholder="Recipient Name"
                  value={filters.recipient}
                  onChange={handleInputChange}
                  isInvalid={!!errors.recipient}
                />
                {errors.recipient && (
                  <Form.Text className="text-danger">{errors.recipient}</Form.Text>
                )}
              </div>
            )}
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <label className="m-b-5">Location</label>
            <Form.Control
              className={styles.customInput}
              type="text"
              name="location"
              placeholder="Any one of: Country, City, State, Town or IP"
              value={filters.location}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <div className="d-flex gap-4 mt-2">
            <div className="d-flex align-items-center">
              <span className={styles.labelText}>At least</span>
              <Form.Control
                className={`${styles.customInput} me-2`}
                type="number"
                name="seen_pages"
                placeholder=""
                min={1}
                value={filters.seen_pages}
                onChange={handleInputChange}
              />
              <span className={styles.labelText}>pages viewed</span>
            </div>
            <div className="d-flex align-items-center">
              <span className={styles.labelText}>At least</span>
              <Form.Control
                className={`${styles.customInput} me-2`}
                type="number"
                name="seen_mins"
                placeholder=""
                min={1}
                value={filters.seen_mins}
                onChange={handleInputChange}
              />
              <span className={styles.labelText}>seconds on page</span>
              <Form.Control
                className={`${styles.customInput} me-2`}
                type="number"
                name="on_page"
                placeholder=""
                min={1}
                value={filters.on_page}
                onChange={handleInputChange}
              />
            </div>
          </div>

        </Col>

        <Col md={6}>
          <div className="d-flex gap-3 mt-2">
            <Form.Group className="mb-2 w-100">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0" style={{whiteSpace: 'nowrap'}}>From</label>
                <div className={styles.dateDisplayWrap} style={{flex: 1}}>
                  <Form.Control
                    className={`${styles.customInput} w-100 ${styles.dateDisplayInput}`}
                    type="text"
                    name="from_date_display"
                    placeholder="DD-MM-YYYY"
                    value={formatDateForDisplay(filters.from_date)}
                    readOnly
                    onClick={() => fromDatePickerRef.current?.showPicker?.()}
                    isInvalid={!!errors.from_date}
                  />
                  <input
                    ref={fromDatePickerRef}
                    type="date"
                    name="from_date"
                    className={styles.datePickerHidden}
                    max={fromDateMax}
                    value={filters.from_date}
                    onChange={handleInputChange}
                    aria-hidden
                    tabIndex={-1}
                  />
                </div>
              </div>
              {errors.from_date && (
                <Form.Text className="text-danger">{errors.from_date}</Form.Text>
              )}
            </Form.Group>
            <Form.Group className="w-100">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0" style={{whiteSpace: 'nowrap'}}>→</label>
                <div className={styles.dateDisplayWrap} style={{flex: 1}}>
                  <Form.Control
                    className={`${styles.customInput} w-100 ${styles.dateDisplayInput}`}
                    type="text"
                    name="to_date_display"
                    placeholder="DD-MM-YYYY"
                    value={formatDateForDisplay(filters.to_date)}
                    readOnly
                    onClick={() => toDatePickerRef.current?.showPicker?.()}
                    isInvalid={!!errors.to_date}
                  />
                  <input
                    ref={toDatePickerRef}
                    type="date"
                    name="to_date"
                    className={styles.datePickerHidden}
                    min={toDateMin}
                    max={maxDateToday}
                    value={filters.to_date}
                    onChange={handleInputChange}
                    aria-hidden
                    tabIndex={-1}
                  />
                </div>
              </div>
              {errors.to_date && (
                <Form.Text className="text-danger">{errors.to_date}</Form.Text>
              )}
            </Form.Group>
          </div>
        </Col>


      </Row>

      <Row className="mt-3">
        <Col md={8}>
          <div className="d-flex align-items-center">
            <>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">Display recipients who actively viewed this KDF for at least this amount of time per page.</p>
                }
              >
                <span className={`${styles.labelText} mr-1`}>
                  Engagement Threshold:
                </span>
              </CustomTooltip>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">{hash.includes("/views")
                    ? "Click on the number to adjust the ET for this Link."
                    : "Display recipients who actively viewed this KDF for at least this amount of time per page."}</p>
                }
              >
                <span className="d-inline-flex align-items-center">
                  {/* <CustomInput
                    placeholder={""}
                    name={"engagement"}
                    value={engagementValue}
                    onChange={(e) => setEngagementValue(e.target.value)}
                    type={"number"}
                    onBlur={(e) => handleEngagement(e.target.value)}
                  /> */}
                <Form.Control
                className={`${styles.customInput} me-2`}
                type="number"
                name={"engagement"}
                placeholder=""
                min={1}
                value={engagementValue}
                onChange={handleEngagementChange}
              />
                  {" "}seconds
                </span>
              </CustomTooltip>
            </>
            <div className="d-flex align-items-center ms-4">
              <span className={`${styles.labelText} me-1`}>Video views:</span>
              <span className="d-inline-flex align-items-center">
                <Form.Control
                  className={`${styles.customInput} me-2`}
                  type="number"
                  name="video_views"
                  placeholder=""
                  min={1}
                  max={100}
                  value={filters.video_views}
                  onChange={handleInputChange}
                />
                {" "}%
              </span>
            </div>
          </div>
        </Col>
        <Col md={4}>
          <div className=" text-right d-flex justify-content-end w-100" style={{marginTop: '35px'}}>
            <Button
              className={`${styles.customBtn} ${styles.secondary} me-2`}
              onClick={handleResetFilters}
            >
              <img src="/icons/reload.svg" />
              Reset
            </Button>
            <Button
              className={`${styles.customBtn} ${styles.primary}`}
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>

          </div>
        </Col>


        {/* <Col md={3} className={styles.borderRight}> */}
        {/* <Form.Group>
            <Form.Control
              className={styles.customInput}
              type="email"
              name="email"
              placeholder="Email"
              value={filters.email}
              onChange={handleInputChange}
            />
          </Form.Group> */}
        {/* <Form.Group className="mt-2">
            <Form.Control
              className={styles.customInput}
              type="text"
              name="location"
              placeholder="Location: Country, City, State, Town"
              value={filters.location}
              onChange={handleInputChange}
            />
          </Form.Group> */}

        {/* </Col> */}
        {/* <Col md={3} className={`${styles.borderRight}`}> */}
        {/* <div className="d-flex align-items-center">
            <span className={styles.labelText}>At least</span>
            <Form.Control
              className={`${styles.customInput} me-2`}
              type="number"
              name="seen_pages"
              placeholder=""
              value={filters.seen_pages}
              onChange={handleInputChange}
            />
            <span className={styles.labelText}>pages viewed</span>
          </div> */}
        {/* <div className="d-flex align-items-center">
            <span className={styles.labelText}>At least</span>
            <Form.Control
              className={`${styles.customInput} me-2`}
              type="number"
              name="seen_mins"
              placeholder=""
              value={filters.seen_mins}
              onChange={handleInputChange}
            />
            <span className={styles.labelText}>Mins</span>
            <Form.Control
              className={`${styles.customInput} me-2`}
              type="number"
              name="on_page"
              placeholder=""
              value={filters.on_page}
              onChange={handleInputChange}
            />
          </div> */}
        {/* </Col> */}
        {/* <Col md={2} className={styles.borderRight}> */}
        {/* <Form.Group className="mb-2">
            <label>From Date</label>
            <Form.Control
              className={`${styles.customInput} w-100`}
              type="date"
              name="from_date"
              value={filters.from_date}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group>
            <label>To Date</label>
            <Form.Control
              className={`${styles.customInput} w-100`}
              type="date"
              name="to_date"
              value={filters.to_date}
              onChange={handleInputChange}
            />
          </Form.Group> */}
        {/* </Col> */}
        {/* <Col md={3}> */}
        {/* <Form.Group>
            {sorting ? (
              <DropdownButton
                className={styles.directionDropDown}
                id="dropdown-basic-button"
                title={
                  selectedOption ? (
                    <>
                      <FontAwesomeIcon
                        icon={selectedOption.icon}
                        className="me-2"
                      />
                      {selectedOption.label}
                    </>
                  ) : (
                    "Select Sorting"
                  )
                }
                onSelect={(eKey) =>
                  handleInputChange({
                    target: { name: "direction", value: eKey },
                  })
                }
                variant="secondary"
              >
                {sortingOptions.map((option) => (
                  <Dropdown.Item key={option.key} eventKey={option.key}>
                    <FontAwesomeIcon icon={option.icon} className="me-2" />
                    {option.label}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            ) : (
              <Form.Control
                className={styles.customInput}
                type="text"
                name="recipient"
                placeholder="Recipient Name"
                value={filters.recipient}
                onChange={handleInputChange}
              />
            )}
          </Form.Group> */}

        {/* </Col> */}
      </Row>
      <Row>
        {/* <Col md={4}>
          <div className="mt-3 text-right d-flex justify-content-end w-100">
            <Button
              className={`${styles.customBtn} ${styles.secondary} me-2`}
              onClick={handleResetFilters}
            >
              <img src="/icons/reload.svg" />
              Reset
            </Button>
            <Button
              className={`${styles.customBtn} ${styles.primary}`}
              onClick={handleApplyFilters}
            >
              Apply Filters
            </Button>

          </div>
        </Col> */}
      </Row>
    </Form>
  );
};

export default StatsFilters;
